/**
 * Access Control Store
 *
 * Zustand store for roles, policies, and permissions state
 */

import { create } from 'zustand';

// Types
export interface Role {
    id: string;
    name: string;
    icon: string;
    description: string | null;
    parent: string | null;
    created_at?: string;
    updated_at?: string;
    // Aggregation fields
    userCount?: number;
    policyCount?: number;
}

// Type alias for Role with counts for table display
export type RoleWithCounts = Role;

// Aggregation result type
export interface AggregateCount {
    count: {
        [key: string]: number;
    };
}

export interface Policy {
    id: string;
    name: string;
    icon: string;
    description: string | null;
    ip_access: string | null;
    enforce_tfa: boolean;
    admin_access: boolean;
    app_access: boolean;
    created_at?: string;
    updated_at?: string;
    // Aggregation fields for user/role counts
    users?: AggregateCount[];
    roles?: AggregateCount[];
    userCount?: number;
    roleCount?: number;
}

// Type alias for Policy with counts for table display
export type PolicyWithCounts = Policy;

// Policy reference can be either a string ID or a nested object with policy details
export interface PolicyRef {
    id: string;
    name?: string;
    icon?: string;
}

export interface Access {
    id: string;
    role: string | null;
    user: string | null;
    policy: string | PolicyRef;
    sort: number | null;
    created_at?: string;
}

// Helper to get policy ID from Access entry
export function getPolicyId(access: Access): string {
    if (typeof access.policy === 'string') {
        return access.policy;
    }
    return access.policy.id;
}

export interface Permission {
    id: string;
    policy: string;
    collection: string;
    action: 'create' | 'read' | 'update' | 'delete' | 'share';
    access?: 'none' | 'partial' | 'full' | string;
    permissions: Record<string, unknown> | null;
    validation: Record<string, unknown> | null;
    presets: Record<string, unknown> | null;
    fields: string[] | null;
}

// Store State
interface AccessControlState {
    // Roles
    roles: Role[];
    selectedRole: Role | null;
    rolesLoading: boolean;

    // Policies
    policies: Policy[];
    selectedPolicy: Policy | null;
    policiesLoading: boolean;

    // Access
    accessEntries: Access[];
    accessLoading: boolean;

    // Permissions
    permissions: Permission[];
    permissionsLoading: boolean;

    // Role Actions
    setRoles: (roles: Role[]) => void;
    setSelectedRole: (role: Role | null) => void;
    setRolesLoading: (loading: boolean) => void;
    addRole: (role: Role) => void;
    updateRole: (id: string, updates: Partial<Role>) => void;
    removeRole: (id: string) => void;

    // Policy Actions
    setPolicies: (policies: Policy[]) => void;
    setSelectedPolicy: (policy: Policy | null) => void;
    setPoliciesLoading: (loading: boolean) => void;
    addPolicy: (policy: Policy) => void;
    updatePolicy: (id: string, updates: Partial<Policy>) => void;
    removePolicy: (id: string) => void;

    // Access Actions
    setAccessEntries: (entries: Access[]) => void;
    setAccessLoading: (loading: boolean) => void;
    addAccess: (access: Access) => void;
    updateAccess: (id: string, updates: Partial<Access>) => void;
    removeAccess: (id: string) => void;

    // Permission Actions
    setPermissions: (permissions: Permission[]) => void;
    setPermissionsLoading: (loading: boolean) => void;
    addPermission: (permission: Permission) => void;
    updatePermission: (id: string, updates: Partial<Permission>) => void;
    removePermission: (id: string) => void;

    // Reset
    reset: () => void;
}

const initialState = {
    roles: [],
    selectedRole: null,
    rolesLoading: false,
    policies: [],
    selectedPolicy: null,
    policiesLoading: false,
    accessEntries: [],
    accessLoading: false,
    permissions: [],
    permissionsLoading: false,
};

export const useAccessControlStore = create<AccessControlState>((set) => ({
    ...initialState,

    // Role Actions
    setRoles: (roles) => set({ roles }),
    setSelectedRole: (selectedRole) => set({ selectedRole }),
    setRolesLoading: (rolesLoading) => set({ rolesLoading }),
    addRole: (role) => set((state) => ({ roles: [...state.roles, role] })),
    updateRole: (id, updates) =>
        set((state) => ({
            roles: state.roles.map((r) => (r.id === id ? { ...r, ...updates } : r)),
            selectedRole:
                state.selectedRole?.id === id
                    ? { ...state.selectedRole, ...updates }
                    : state.selectedRole,
        })),
    removeRole: (id) =>
        set((state) => ({
            roles: state.roles.filter((r) => r.id !== id),
            selectedRole: state.selectedRole?.id === id ? null : state.selectedRole,
        })),

    // Policy Actions
    setPolicies: (policies) => set({ policies }),
    setSelectedPolicy: (selectedPolicy) => set({ selectedPolicy }),
    setPoliciesLoading: (policiesLoading) => set({ policiesLoading }),
    addPolicy: (policy) => set((state) => ({ policies: [...state.policies, policy] })),
    updatePolicy: (id, updates) =>
        set((state) => ({
            policies: state.policies.map((p) => (p.id === id ? { ...p, ...updates } : p)),
            selectedPolicy:
                state.selectedPolicy?.id === id
                    ? { ...state.selectedPolicy, ...updates }
                    : state.selectedPolicy,
        })),
    removePolicy: (id) =>
        set((state) => ({
            policies: state.policies.filter((p) => p.id !== id),
            selectedPolicy: state.selectedPolicy?.id === id ? null : state.selectedPolicy,
        })),

    // Access Actions
    setAccessEntries: (accessEntries) => set({ accessEntries }),
    setAccessLoading: (accessLoading) => set({ accessLoading }),
    addAccess: (access) =>
        set((state) => ({ accessEntries: [...state.accessEntries, access] })),
    updateAccess: (id, updates) =>
        set((state) => ({
            accessEntries: state.accessEntries.map((a) =>
                a.id === id ? { ...a, ...updates } : a
            ),
        })),
    removeAccess: (id) =>
        set((state) => ({
            accessEntries: state.accessEntries.filter((a) => a.id !== id),
        })),

    // Permission Actions
    setPermissions: (permissions) => set({ permissions }),
    setPermissionsLoading: (permissionsLoading) => set({ permissionsLoading }),
    addPermission: (permission) =>
        set((state) => ({ permissions: [...state.permissions, permission] })),
    updatePermission: (id, updates) =>
        set((state) => ({
            permissions: state.permissions.map((p) =>
                p.id === id ? { ...p, ...updates } : p
            ),
        })),
    removePermission: (id) =>
        set((state) => ({
            permissions: state.permissions.filter((p) => p.id !== id),
        })),

    // Reset
    reset: () => set(initialState),
}));
